import { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Category, Segment } from '@/data/mockData';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

interface CategoryAccordionProps {
  categories: Category[];
  selectedSegments: string[];
  onSegmentToggle: (segmentId: string) => void;
  onCategoryToggle: (categoryId: string) => void;
  expandedCategoryIds?: string[];
}

const CategoryAccordion = ({ 
  categories, 
  selectedSegments, 
  onSegmentToggle, 
  onCategoryToggle,
  expandedCategoryIds = []
}: CategoryAccordionProps) => {
  const [openCategories, setOpenCategories] = useState<string[]>([]);

  // Calculer les catégories qui contiennent des segments sélectionnés
  const categoriesWithSelectedSegments = useMemo(() => {
    return categories
      .filter((category) =>
        category.segments.some((segment) => selectedSegments.includes(segment.id))
      )
      .map((category) => category.id);
  }, [categories, selectedSegments]);

  // Synchroniser openCategories avec expandedCategoryIds quand une recherche est active
  // ou avec les catégories contenant des segments sélectionnés quand la recherche est vide
  useEffect(() => {
    if (expandedCategoryIds.length > 0) {
      // Recherche active : déplier les catégories qui matchent
      setOpenCategories(expandedCategoryIds);
    } else {
      // Recherche vide : replier toutes les catégories SAUF celles avec segments sélectionnés
      setOpenCategories(categoriesWithSelectedSegments);
    }
  }, [expandedCategoryIds, categoriesWithSelectedSegments]);

  const toggleCategory = (categoryId: string) => {
    setOpenCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const isCategorySelected = (category: Category) => {
    return category.segments.every(segment => 
      selectedSegments.includes(segment.id)
    );
  };

  const isCategoryPartiallySelected = (category: Category) => {
    return category.segments.some(segment => 
      selectedSegments.includes(segment.id)
    ) && !isCategorySelected(category);
  };

  return (
    <div className="space-y-2">
      {categories.map((category) => (
        <div key={category.id} className="border border-[#ECEDEE] rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 p-3 hover:bg-muted/50 transition-colors">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggleCategory(category.id)}
              className="p-0 h-auto hover:bg-transparent"
            >
              {openCategories.includes(category.id) ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
            
            <Checkbox
              checked={isCategorySelected(category)}
              onCheckedChange={() => onCategoryToggle(category.id)}
              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            
            <span 
              className="font-medium cursor-pointer flex-1"
              onClick={() => toggleCategory(category.id)}
            >
              {category.name}
            </span>
          </div>
          
          {openCategories.includes(category.id) && (
            <div className="border-t border-[#ECEDEE] bg-muted/20">
              {category.segments.map((segment) => (
                <div 
                  key={segment.id}
                  className="flex items-center gap-2 p-3 pl-8 hover:bg-muted/50 transition-colors"
                >
                  <Checkbox
                    checked={selectedSegments.includes(segment.id)}
                    onCheckedChange={() => onSegmentToggle(segment.id)}
                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  />
                  <span className="text-sm">{segment.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CategoryAccordion;